import { type CSSProperties, type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, ErrorState, StatusPill } from '@ui-kit';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGrpcHealth } from '../hooks/useGrpcHealth';
import { useGrpcEcho } from '../hooks/useGrpcEcho';
import { IntegrationCommonUsagePanel } from '../components/IntegrationCommonUsagePanel';
import { OffsetTimeline } from '../components/CallTimeline';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { grpcEchoFormSchema, type GrpcEchoFormValues } from '../schema/integrationHub.schema';
import type { ApiError } from '../types/integrationHub';

/**
 * GrpcConsolePage — gRPC echo/health 콘솔 (/integration-hub/grpc · §Δ.3)
 * 5상태: loading(health skeleton + 전송 spinner) / empty(전송 전) / error(INTGHUB-502-001/503-001) / default(replies 렌더) / dialog(N/A — 인라인 콘솔)
 */
export function GrpcConsolePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const healthQuery = useGrpcHealth();
  const echoMutation = useGrpcEcho();

  const [echoError, setEchoError] = useState<ApiError | null>(null);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<GrpcEchoFormValues>({
    resolver: zodResolver(grpcEchoFormSchema),
    defaultValues: { message: '', stream: false },
  });
  const streamMode = watch('stream');

  const onSubmit = useCallback(
    (values: GrpcEchoFormValues) => {
      setEchoError(null);
      echoMutation.mutate(
        { message: values.message, stream: values.stream },
        { onError: (err) => setEchoError(err as ApiError) }
      );
    },
    [echoMutation]
  );

  const health = healthQuery.data;
  const isHealthLoading = healthQuery.isLoading;
  const isHealthError = healthQuery.isError;
  const is403 = isHealthError && (healthQuery.error as unknown as ApiError)?.code === 'AUTH-403-001';

  const result = echoMutation.data ?? null;
  const isEchoPending = echoMutation.isPending;

  return (
    <div style={{ padding: 0 }}>
      <PageHeader
        title={t('integrationHub.grpc.title')}
        breadcrumb={<span>{t('integrationHub.grpc.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => navigate('/integration-hub')} style={tabButtonStyle}>
              {t('integrationHub.tab.endpoints')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/storage')} style={tabButtonStyle}>
              {t('integrationHub.tab.storage')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/history')} style={tabButtonStyle}>
              {t('integrationHub.tab.history')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/tcp')} style={tabButtonStyle}>
              {t('integrationHub.tab.tcp')}
            </button>
          </div>
        }
      />

      {/* health loading skeleton */}
      {isHealthLoading && (
        <div aria-label={t('integrationHub.grpc.status.loadingAriaLabel')} aria-busy="true" style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + (i * 15) % 40}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* health error */}
      {isHealthError && (
        <div style={cardStyle}>
          {is403 ? (
            <ErrorState
              title={t('integrationHub.error.AUTH-403-001')}
              message={t('integrationHub.error.forbidden')}
              onRetry={() => navigate('/')}
            />
          ) : (
            <ErrorState
              title={t('integrationHub.error.loadFailed')}
              message={(healthQuery.error as unknown as ApiError)?.message ?? t('integrationHub.error.network')}
              onRetry={() => void healthQuery.refetch()}
            />
          )}
        </div>
      )}

      {/* default — health 카드 + echo 콘솔 */}
      {!isHealthLoading && !isHealthError && health && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <section aria-label={t('integrationHub.grpc.status.title')} style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('integrationHub.grpc.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('integrationHub.grpc.status.serving')}>
                <StatusPill tone={health.status === 'SERVING' ? 'success' : health.status === 'NOT_SERVING' ? 'danger' : 'neutral'}>
                  {health.status}
                </StatusPill>
              </InfoField>
              <InfoField label={t('integrationHub.grpc.status.port')}>
                <code style={monoStyle}>{health.boundPort}</code>
              </InfoField>
              <InfoField label={t('integrationHub.grpc.status.demoEnabled')}>
                <span style={{ color: health.demoEnabled ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                  {health.demoEnabled ? '✓' : '✗'}
                </span>
              </InfoField>
              <InfoField label={t('integrationHub.grpc.status.channelTarget')}>
                <code style={monoStyle}>{health.channelTarget}</code>
              </InfoField>
            </div>
            {!health.serverRunning && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('integrationHub.error.INTGHUB-503-001')}
              </p>
            )}
          </section>

          {/* echo 입력 */}
          <section aria-label={t('integrationHub.grpc.input.title')} style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('integrationHub.grpc.input.title')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '1 1 240px' }}>
                <input
                  type="text"
                  placeholder={t('integrationHub.grpc.input.placeholder')}
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'grpc-message-error' : undefined}
                  style={inputStyle}
                  {...register('message')}
                />
                {errors.message && (
                  <span id="grpc-message-error" role="alert" style={errorTextStyle}>
                    {errors.message.message}
                  </span>
                )}
              </div>

              {/* unary / streaming 토글 */}
              <input type="hidden" {...register('stream')} />
              <div role="group" aria-label={t('integrationHub.grpc.mode.label')} style={{ display: 'flex', gap: 'var(--spacing-1)', padding: 'var(--spacing-2) 0' }}>
                <button
                  type="button"
                  aria-pressed={!streamMode}
                  onClick={() => setValue('stream', false, { shouldValidate: true })}
                  style={toggleButtonStyle(!streamMode)}
                >
                  {t('integrationHub.grpc.mode.unary')}
                </button>
                <button
                  type="button"
                  aria-pressed={streamMode}
                  onClick={() => setValue('stream', true, { shouldValidate: true })}
                  style={toggleButtonStyle(!!streamMode)}
                >
                  {t('integrationHub.grpc.mode.stream')}
                </button>
              </div>

              <ButtonGuard requirePermission="INTEGRATION_INVOKE">
                {({ disabled, reason }) => (
                  <button
                    type="submit"
                    disabled={disabled || isEchoPending}
                    title={reason}
                    style={{
                      ...primaryButtonStyle,
                      opacity: disabled || isEchoPending ? 0.6 : 1,
                      cursor: disabled || isEchoPending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isEchoPending ? '...' : t('integrationHub.grpc.button.echo')}
                  </button>
                )}
              </ButtonGuard>
            </form>

            {/* loading — 전송 중 */}
            {isEchoPending && (
              <p aria-live="polite" style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('integrationHub.grpc.status.sending')}
              </p>
            )}

            {/* error */}
            {echoError && !isEchoPending && (
              <div role="alert" style={errorBoxStyle(echoError.code === 'INTGHUB-503-001')}>
                {echoError.code === 'INTGHUB-503-001' ? (
                  <strong>{t('integrationHub.error.INTGHUB-503-001')}</strong>
                ) : (
                  <>
                    <strong>{t('integrationHub.echoConsole.error.unreachable')}</strong>
                    <button type="button" onClick={() => void healthQuery.refetch()} style={{ ...secondaryButtonStyle, marginLeft: 'var(--spacing-3)' }}>
                      {t('integrationHub.button.retry')}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* empty — 전송 전 */}
            {!result && !echoError && !isEchoPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('integrationHub.grpc.empty.description')}
              </p>
            )}

            {/* default — 결과 */}
            {result && !isEchoPending && (
              <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <StatusPill tone={result.streaming ? 'info' : 'neutral'}>
                    {result.streaming ? t('integrationHub.grpc.mode.stream') : t('integrationHub.grpc.mode.unary')}
                  </StatusPill>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                    {t('integrationHub.grpc.status.channelTarget')}: {result.channelTarget}
                  </span>
                </div>

                <div>
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
                    {t('integrationHub.grpc.reply.title')}
                  </p>
                  {!result.streaming ? (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {result.replies.map((r, i) => (
                        <li key={i} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', padding: 'var(--spacing-2) 0' }}>
                          {r.message} <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>({t('integrationHub.grpc.reply.offset')}: {r.receivedOffsetMs}ms)</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <OffsetTimeline frames={result.replies} totalMs={result.totalMs} />
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* 공통 모듈 패널 */}
      <IntegrationCommonUsagePanel />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

function toggleButtonStyle(active: boolean): CSSProperties {
  return {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: active ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
    color: active ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
    border: `1px solid ${active ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };
}

function errorBoxStyle(isDisabled: boolean): CSSProperties {
  return {
    marginTop: 'var(--spacing-3)',
    padding: 'var(--spacing-3)',
    border: `1px solid ${isDisabled ? 'var(--color-warning)' : 'var(--color-danger)'}`,
    borderRadius: 'var(--radius-md)',
    background: 'var(--color-surface-muted)',
    fontSize: 'var(--font-size-sm)',
    color: isDisabled ? 'var(--color-warning)' : 'var(--color-danger)',
  };
}

const tabButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const primaryButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const cardStyle: CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const inputStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  width: '100%',
};

const monoStyle: CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const errorTextStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};
